import {
  SPECIAL_BEER_DUTY_OPTIONS,
  createSpecialBeerDuty,
  isSpecialBeerDutyKind,
} from "../../../shared/domain/schedule";
import { SCHEDULE_FIELDS, SCHEDULE_TIME_SLOTS } from "../../domain/schedule";
import type {
  PregameSchedulePatch,
  RevisionedCommandResult,
} from "../../hooks/teamTypes";
import type { BeerDuty, PlayerListItem } from "../../shared/types/contracts";
import { Button } from "../../shared/ui/button";
import { ButtonGroup } from "../../shared/ui/button-group";
import { Card, CardContent } from "../../shared/ui/card";
import { Input } from "../../shared/ui/input";
import { InlineSaveInput } from "../../shared/ui/inline-save-input";
import type { LineupMode } from "./setupTypes";
import { actionKeys } from "../../hooks/actionKeys";
import type { IntentStatusView } from "../../hooks/useIntentStatus";
import { useCallback, useEffect, useRef, useState } from "react";

interface ScheduleDetailsDraft {
  scheduledDate: string | null;
  timeSlot: PregameSchedulePatch["timeSlot"];
  field: PregameSchedulePatch["field"];
  beerDuty: BeerDuty | null;
}

function detailsFromProps({
  scheduledDate,
  timeSlot,
  field,
  beerDuty,
}: Pick<
  GameSetupStepProps,
  "scheduledDate" | "timeSlot" | "field" | "beerDuty"
>): ScheduleDetailsDraft {
  return {
    scheduledDate: scheduledDate ?? null,
    timeSlot: timeSlot ?? null,
    field: field ?? null,
    beerDuty: beerDuty ?? null,
  };
}

interface SubmittedScheduleDetails {
  patch: PregameSchedulePatch;
  committedRevision: number | null;
}

interface GameSetupStepProps {
  players: PlayerListItem[];
  lineupMode: LineupMode;
  opponentName: string;
  opponentLocked: boolean;
  isHome: boolean;
  authoritativeRevision: number;
  scheduledDate: string | null | undefined;
  timeSlot: PregameSchedulePatch["timeSlot"] | undefined;
  field: PregameSchedulePatch["field"] | undefined;
  beerDuty: BeerDuty | null | undefined;
  onSetOpponentName: (opponentName: string) => Promise<RevisionedCommandResult>;
  onSetHomeTeam: (isHome: boolean) => Promise<boolean>;
  onSetScheduleDetails?:
    | ((patch: PregameSchedulePatch) => Promise<RevisionedCommandResult>)
    | undefined;
  onShowDefense: () => void;
  actionStatus: IntentStatusView;
}

export function GameSetupStep({
  players,
  lineupMode,
  opponentName,
  opponentLocked,
  isHome,
  authoritativeRevision,
  scheduledDate,
  timeSlot,
  field,
  beerDuty,
  onSetOpponentName,
  onSetHomeTeam,
  onSetScheduleDetails,
  onShowDefense,
  actionStatus,
}: GameSetupStepProps) {
  const pregameEditable = lineupMode === "pregame";
  const liveEditable = lineupMode === "live";
  const homePending = actionStatus.isPending(actionKeys.homeTeam);
  const detailsPending = actionStatus.isPending(actionKeys.scheduleDetails);
  const opponentUnknown =
    actionStatus.phaseFor(actionKeys.opponentName) === "outcome_unknown";
  const homeUnknown =
    actionStatus.phaseFor(actionKeys.homeTeam) === "outcome_unknown";
  const detailsUnknown =
    actionStatus.phaseFor(actionKeys.scheduleDetails) === "outcome_unknown";
  const authoritativeDetails = detailsFromProps({
    scheduledDate,
    timeSlot,
    field,
    beerDuty,
  });
  const authoritativeDetailsRef = useRef(authoritativeDetails);
  authoritativeDetailsRef.current = authoritativeDetails;
  const authoritativeRevisionRef = useRef(authoritativeRevision);
  authoritativeRevisionRef.current = authoritativeRevision;
  const [detailsDraft, setDetailsDraft] = useState(authoritativeDetails);
  const [submittedDetails, setSubmittedDetails] =
    useState<SubmittedScheduleDetails | null>(null);
  const submittedDetailsRef = useRef(submittedDetails);
  submittedDetailsRef.current = submittedDetails;

  const settleDetails = useCallback((): boolean => {
    const submitted = submittedDetailsRef.current;
    if (
      !submitted ||
      submitted.committedRevision === null ||
      authoritativeRevisionRef.current < submitted.committedRevision
    ) {
      return false;
    }
    submittedDetailsRef.current = null;
    setSubmittedDetails(null);
    setDetailsDraft(authoritativeDetailsRef.current);
    return true;
  }, []);

  useEffect(() => {
    if (settleDetails()) return;
    if (!submittedDetailsRef.current) {
      setDetailsDraft(authoritativeDetailsRef.current);
    }
  }, [
    authoritativeRevision,
    beerDuty,
    field,
    scheduledDate,
    settleDetails,
    timeSlot,
  ]);

  const saveDetails = async (patch: PregameSchedulePatch) => {
    if (!onSetScheduleDetails || submittedDetailsRef.current) return;
    const submitted = { patch, committedRevision: null };
    submittedDetailsRef.current = submitted;
    setSubmittedDetails(submitted);
    setDetailsDraft((current) => ({ ...current, ...patch }));
    const result = await onSetScheduleDetails(patch);
    if (submittedDetailsRef.current !== submitted) return;
    if (!result?.succeeded || result.committedRevision === null) {
      submittedDetailsRef.current = null;
      setSubmittedDetails(null);
      setDetailsDraft(authoritativeDetailsRef.current);
      return;
    }
    const acknowledged = {
      ...submitted,
      committedRevision: result.committedRevision,
    };
    submittedDetailsRef.current = acknowledged;
    setSubmittedDetails(acknowledged);
    settleDetails();
  };
  const detailsSaving = detailsPending || submittedDetails !== null;

  return (
    <Card className="shadow-none">
      <CardContent className="grid gap-2 pt-4">
        <h3 className="m-0 font-mono text-sm font-bold tracking-[0.08em] text-warning uppercase">
          3 · TONIGHT'S GAME
        </h3>
        <InlineSaveInput
          value={opponentName}
          authoritativeRevision={authoritativeRevision}
          aria-label="Opponent"
          disabled={lineupMode === "locked" || opponentLocked}
          onSave={onSetOpponentName}
          placeholder="Opponent"
          unknown={opponentUnknown}
        />
        <div className="flex items-center gap-2">
          <span className="shrink-0 font-mono text-xs font-bold text-foreground-muted">
            WE ARE
          </span>
          <ButtonGroup className="flex-1" aria-busy={homePending || undefined}>
            <Button
              type="button"
              onClick={() => onSetHomeTeam(false)}
              disabled={!pregameEditable || homePending}
              size="sm"
              variant={!isHome ? "selected" : "outline"}
              aria-pressed={!isHome}
              className="flex-1"
            >
              AWAY
            </Button>
            <Button
              type="button"
              onClick={() => onSetHomeTeam(true)}
              disabled={!pregameEditable || homePending}
              size="sm"
              variant={isHome ? "selected" : "outline"}
              aria-pressed={isHome}
              className="flex-1"
            >
              HOME
            </Button>
          </ButtonGroup>
        </div>
        <div className="grid min-w-0 gap-2 md:grid-cols-2">
          <label className="grid min-w-0 gap-1 text-xs font-bold text-foreground-muted">
            DATE
            <Input
              type="date"
              disabled={!pregameEditable || detailsSaving}
              className="block min-w-0 max-w-full appearance-none [-webkit-appearance:none] px-2 text-sm leading-[var(--control-input)]"
              min={new Date().toISOString().slice(0, 10)}
              value={detailsDraft.scheduledDate ?? ""}
              onChange={(event) =>
                void saveDetails({
                  scheduledDate: event.target.value || null,
                })
              }
            />
          </label>
          <label className="grid min-w-0 gap-1 text-xs font-bold text-foreground-muted">
            TIME
            <select
              aria-label="Game time"
              disabled={!pregameEditable || detailsSaving}
              className="h-10 w-full min-w-0 max-w-full rounded-[var(--radius-sm)] border border-border bg-background px-2 text-sm"
              value={detailsDraft.timeSlot ?? ""}
              onChange={(event) =>
                void saveDetails({
                  timeSlot:
                    (event.target.value as PregameSchedulePatch["timeSlot"]) ||
                    null,
                })
              }
            >
              <option value="">Select time</option>
              {SCHEDULE_TIME_SLOTS.map((slot) => (
                <option key={slot.value} value={slot.value}>
                  {slot.label} · {slot.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="grid min-w-0 gap-2 md:grid-cols-2">
          <label className="grid min-w-0 gap-1 text-xs font-bold text-foreground-muted">
            FIELD
            <select
              aria-label="Game field"
              disabled={!pregameEditable || detailsSaving}
              className="h-10 w-full min-w-0 max-w-full rounded-[var(--radius-sm)] border border-border bg-background px-2 text-sm"
              value={detailsDraft.field ?? ""}
              onChange={(event) =>
                void saveDetails({
                  field:
                    (event.target.value as PregameSchedulePatch["field"]) ||
                    null,
                })
              }
            >
              <option value="">Select field</option>
              {SCHEDULE_FIELDS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="grid min-w-0 gap-1 text-xs font-bold text-foreground-muted">
            BEER DUTY
            <select
              aria-label="Beer duty"
              disabled={!pregameEditable || detailsSaving}
              className="h-10 w-full min-w-0 max-w-full rounded-[var(--radius-sm)] border border-border bg-background px-2 text-sm"
              value={
                detailsDraft.beerDuty?.kind === "player"
                  ? `player:${detailsDraft.beerDuty.playerId}`
                  : detailsDraft.beerDuty &&
                      isSpecialBeerDutyKind(detailsDraft.beerDuty.kind)
                    ? detailsDraft.beerDuty.kind
                    : ""
              }
              onChange={(event) => {
                const value = event.target.value;
                if (isSpecialBeerDutyKind(value)) {
                  void saveDetails({
                    beerDuty: createSpecialBeerDuty(value),
                  });
                } else if (value) {
                  const player = players.find(
                    (candidate) =>
                      candidate.id === value.slice("player:".length),
                  );
                  if (player) {
                    void saveDetails({
                      beerDuty: {
                        kind: "player",
                        playerId: player.id,
                        nameSnapshot: player.name,
                      },
                    });
                  }
                } else {
                  void saveDetails({ beerDuty: null });
                }
              }}
            >
              <option value="">Select beer duty</option>
              {SPECIAL_BEER_DUTY_OPTIONS.map((option) => (
                <option key={option.kind} value={option.kind}>
                  {option.editorLabel}
                </option>
              ))}
              {players
                .filter((player) => player.active)
                .map((player) => (
                  <option key={player.id} value={`player:${player.id}`}>
                    {player.name}
                  </option>
                ))}
            </select>
          </label>
        </div>
        <div
          aria-live="polite"
          className="min-h-4 font-mono text-[0.62rem] font-bold tracking-[0.08em] text-foreground-muted"
        >
          {homePending
            ? homeUnknown
              ? "CHECKING HOME / AWAY…"
              : "SAVING HOME / AWAY…"
            : detailsSaving
              ? detailsUnknown
                ? "CHECKING GAME DETAILS…"
                : "SAVING GAME DETAILS…"
              : ""}
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-border pt-2">
          <div className="min-w-0 text-xs text-foreground-muted">
            {liveEditable
              ? "Update the defensive rotation for the active game."
              : lineupMode === "locked"
                ? "The completed game's defensive rotation is locked."
                : "Set the pitcher and rotation before first pitch."}
          </div>
          <Button
            type="button"
            onClick={onShowDefense}
            disabled={lineupMode === "locked"}
            size="sm"
            variant="outline"
          >
            DEFENSE →
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
